import { useMemo } from 'react';
import { ProductModule } from '@erp/shared-types';
import { useGetModuleConfig } from '@/api/org/config/modules';
import type { ArBasisLock } from '@/components/accounts-receivable/ar-report-filters';

/**
 * Reads the AR module config and reports whether the aging-basis selector
 * should be locked across statement / report surfaces, and which basis to
 * force. Both flags default to "unlocked" so surfaces behave exactly as before
 * until an org opts in via Organization Settings → Modules → AR Invoicing.
 *
 * Returns a referentially stable object (memoized on the underlying
 * primitives) so it is safe to drop into `useMemo` / `useEffect` dependency
 * arrays without retriggering on every render.
 */
export function useArStatementBasisLock(): ArBasisLock {
  const { data } = useGetModuleConfig(ProductModule.AR_INVOICING);
  const lockEnabled = data?.config?.ar?.lockStatementBasis === true;
  const defaultBasis = data?.config?.ar?.statementDefaultBasis ?? null;

  return useMemo(
    () => ({
      lockBasis: lockEnabled && defaultBasis != null,
      lockedBasis: defaultBasis,
    }),
    [lockEnabled, defaultBasis],
  );
}
